import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  ShoppingCart,
  RefreshCw,
  Settings,
  Layers,
  Brain,
  BarChart3,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Zap,
  DollarSign,
  Package,
  TrendingUp,
  Eye,
  RotateCcw,
  Play,
  Save,
  ChevronRight,
  ExternalLink,
  Sparkles,
  Search,
  SplitSquareHorizontal,
  ToggleLeft,
  ToggleRight,
  Megaphone,
  Pause,
} from 'lucide-react'
import { ebayApi } from '@/lib/api'

// =========================================================================
// Types
// =========================================================================

interface DashboardData {
  listings: { published: number; total: number; by_status: Record<string, number> }
  ai: { total_generated: number; total_tokens: number; total_cost_eur: number; by_model: Record<string, number> }
  consolidated_refs: number
  surcharge: { pct: string; fixed: string }
  last_stock_sync: any
  last_price_sync: any
}

interface AiStatusData {
  total_generated: number
  total_consolidated: number
  pending: number
  total_tokens: number
  total_cost_eur: number
  by_model: Record<string, number>
}

interface AiContentItem {
  id: number
  sku: string
  title: string
  description_html: string
  aspects: Record<string, string[]>
  product_count: number
  model_used: string
  tokens_used: number
  cost_eur: number
  generated_at: string
}

interface SelectionData {
  layer_a_count: number
  layer_b_count: number
  layer_c_count: number
  total_slots: number
  stats: any
}

// =========================================================================
// Tab Button
// =========================================================================

function TabButton({ active, onClick, icon: Icon, label }: {
  active: boolean; onClick: () => void; icon: any; label: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
        active
          ? 'bg-blue-600 text-white shadow-md'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

// =========================================================================
// KPI Card
// =========================================================================

function KpiCard({ label, value, icon: Icon, color = 'blue', subtitle }: {
  label: string; value: string | number; icon: any; color?: string; subtitle?: string
}) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  }
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  )
}

// =========================================================================
// Dashboard Tab
// =========================================================================

function DashboardTab() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const result = await ebayApi.getDashboard()
      setData(result)
    } catch (e) {
      console.error('Dashboard load error:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSync = async (type: 'stock' | 'prices') => {
    setSyncing(type)
    try {
      if (type === 'stock') await ebayApi.syncStock()
      else await ebayApi.syncPrices()
      await load()
    } catch (e) {
      console.error(`Sync ${type} error:`, e)
    } finally {
      setSyncing(null)
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
  if (!data) return <div className="text-center py-20 text-gray-500">Error cargando dashboard</div>

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Publicados" value={data.listings.published} icon={ShoppingCart} color="green" />
        <KpiCard label="Total listings" value={data.listings.total} icon={Package} color="blue" />
        <KpiCard label="Refs consolidadas" value={data.consolidated_refs} icon={Layers} color="purple" />
        <KpiCard label="Fichas IA" value={data.ai.total_generated} icon={Brain} color="amber" />
        <KpiCard label="Coste IA" value={`${data.ai.total_cost_eur.toFixed(2)}€`} icon={DollarSign} color="rose" />
      </div>

      {/* Status by listing */}
      {data.listings.by_status && Object.keys(data.listings.by_status).length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estado de listings</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.listings.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-sm">
                <span className={`w-2 h-2 rounded-full ${
                  status === 'published' ? 'bg-green-500' :
                  status === 'error' ? 'bg-red-500' :
                  status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
                }`} />
                <span className="text-gray-700 dark:text-gray-300">{status}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync Actions */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Sincronizacion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Stock</p>
              <p className="text-xs text-gray-500">
                {data.last_stock_sync
                  ? `Ultima: ${new Date(data.last_stock_sync.started_at).toLocaleString('es-ES')}`
                  : 'Nunca sincronizado'}
              </p>
            </div>
            <button
              onClick={() => handleSync('stock')}
              disabled={syncing !== null}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {syncing === 'stock' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync Stock
            </button>
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Precios</p>
              <p className="text-xs text-gray-500">
                {data.last_price_sync
                  ? `Ultima: ${new Date(data.last_price_sync.started_at).toLocaleString('es-ES')}`
                  : 'Nunca sincronizado'}
              </p>
            </div>
            <button
              onClick={() => handleSync('prices')}
              disabled={syncing !== null}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {syncing === 'prices' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync Precios
            </button>
          </div>
        </div>
      </div>

      {/* Surcharge info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Sobrecoste actual</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Porcentual: <span className="font-semibold">{(parseFloat(data.surcharge.pct) * 100).toFixed(0)}%</span>
          {' + '}
          Fijo: <span className="font-semibold">{parseFloat(data.surcharge.fixed).toFixed(2)}€</span>
        </p>
      </div>
    </div>
  )
}

// =========================================================================
// Config Tab
// =========================================================================

function PromotedListingsCard() {
  const [status, setStatus] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(false)
  const [actionMsg, setActionMsg] = useState('')
  const [adRate, setAdRate] = useState('5.0')
  const [savingRate, setSavingRate] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      const res = await ebayApi.promotedStatus()
      setStatus(res)
      setAdRate(res.ad_rate || '5.0')
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadStatus() }, [loadStatus])

  const handleSetup = async () => {
    setActing(true)
    setActionMsg('')
    try {
      const res = await ebayApi.promotedSetup()
      setActionMsg(`Campana creada. ${res.new_added || 0} productos anadidos.`)
      await loadStatus()
    } catch (e: any) {
      setActionMsg(`Error: ${e?.response?.data?.detail || e.message}`)
    } finally {
      setActing(false)
    }
  }

  const handlePause = async () => {
    setActing(true)
    try {
      await ebayApi.promotedPause()
      await loadStatus()
    } catch (e: any) {
      setActionMsg(`Error: ${e?.response?.data?.detail || e.message}`)
    } finally {
      setActing(false)
    }
  }

  const handleResume = async () => {
    setActing(true)
    try {
      await ebayApi.promotedResume()
      await loadStatus()
    } catch (e: any) {
      setActionMsg(`Error: ${e?.response?.data?.detail || e.message}`)
    } finally {
      setActing(false)
    }
  }

  const handleSync = async () => {
    setActing(true)
    setActionMsg('')
    try {
      const res = await ebayApi.promotedSync()
      setActionMsg(`Sync: ${res.new_added || 0} nuevos, ${res.total_ads || 0} total.`)
      await loadStatus()
    } catch (e: any) {
      setActionMsg(`Error: ${e?.response?.data?.detail || e.message}`)
    } finally {
      setActing(false)
    }
  }

  const handleToggle = async () => {
    setActing(true)
    try {
      const newVal = status?.enabled ? 'false' : 'true'
      await ebayApi.updateConfig('promoted_enabled', newVal)
      await loadStatus()
    } catch {
    } finally {
      setActing(false)
    }
  }

  const handleSaveRate = async () => {
    setSavingRate(true)
    try {
      const val = Math.max(2, Math.min(20, parseFloat(adRate) || 5))
      await ebayApi.updateConfig('promoted_ad_rate', val.toString())
      setAdRate(val.toString())
    } catch {
    } finally {
      setSavingRate(false)
    }
  }

  if (loading) return <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6"><Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" /></div>

  const campaignStatus = status?.campaign_status
  const isRunning = campaignStatus === 'RUNNING'
  const isPaused = campaignStatus === 'PAUSED'
  const hasNoCampaign = !status?.campaign_id
  const examplePrice = 33.78
  const adCost = (examplePrice * (parseFloat(adRate) || 5) / 100).toFixed(2)

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Anuncios patrocinados</h3>
        </div>
        {campaignStatus && (
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
            isRunning ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
            isPaused ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {isRunning ? 'Activa' : isPaused ? 'Pausada' : campaignStatus}
          </span>
        )}
      </div>

      <div className="space-y-4">
        {/* Toggle enable */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Promoted Listings Standard (CPS)</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Solo pagas si vendes. eBay cobra el porcentaje sobre el precio total de la venta.
            </p>
          </div>
          <button onClick={handleToggle} disabled={acting} className="flex-shrink-0 ml-4">
            {acting ? <Loader2 className="w-10 h-10 animate-spin text-gray-400" /> :
              status?.enabled ? <ToggleRight className="w-10 h-10 text-green-500 cursor-pointer" /> :
              <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer" />}
          </button>
        </div>

        {status?.enabled && (
          <>
            {/* Ad rate */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Porcentaje de anuncio (ad rate)
              </label>
              <div className="flex items-center gap-3">
                <div className="relative w-32">
                  <input
                    type="number"
                    min="2"
                    max="20"
                    step="0.5"
                    value={adRate}
                    onChange={(e) => setAdRate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                </div>
                <button
                  onClick={handleSaveRate}
                  disabled={savingRate}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  {savingRate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Con un {adRate}%, un producto de {examplePrice.toFixed(2)}EUR genera {adCost}EUR de coste publicitario. Recomendado: 5%.
              </p>
            </div>

            {/* Stats */}
            {status?.campaign_id && (
              <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                  <span><strong className="text-gray-900 dark:text-white">{status.ad_count ?? '?'}</strong> productos promocionados</span>
                  <span>de <strong className="text-gray-900 dark:text-white">{status.total_published ?? '?'}</strong> publicados</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap gap-2">
              {hasNoCampaign ? (
                <button
                  onClick={handleSetup}
                  disabled={acting}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
                >
                  {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Activar campana
                </button>
              ) : (
                <>
                  {isRunning && (
                    <button
                      onClick={handlePause}
                      disabled={acting}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
                      Pausar
                    </button>
                  )}
                  {isPaused && (
                    <button
                      onClick={handleResume}
                      disabled={acting}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                      Reanudar
                    </button>
                  )}
                  <button
                    onClick={handleSync}
                    disabled={acting}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Sincronizar ads
                  </button>
                </>
              )}
            </div>

            {actionMsg && (
              <p className={`text-sm ${actionMsg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {actionMsg}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ConfigTab() {
  const [pct, setPct] = useState('15')
  const [fixed, setFixed] = useState('0')
  const [syncEnabled, setSyncEnabled] = useState(false)
  const [maxNewPerSync, setMaxNewPerSync] = useState('50')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [togglingSync, setTogglingSync] = useState(false)

  useEffect(() => {
    ebayApi.getConfig().then((res) => {
      const config = res.config || {}
      if (config.surcharge_pct) setPct((parseFloat(config.surcharge_pct) * 100).toFixed(1))
      if (config.surcharge_fixed) setFixed(config.surcharge_fixed)
      setSyncEnabled(config.ebay_sync_enabled === 'true')
      if (config.ebay_max_new_per_sync) setMaxNewPerSync(config.ebay_max_new_per_sync)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const handleToggleSync = async () => {
    setTogglingSync(true)
    try {
      const newValue = !syncEnabled
      await ebayApi.updateConfig('ebay_sync_enabled', newValue.toString())
      setSyncEnabled(newValue)
    } catch (e) {
      console.error('Toggle sync error:', e)
    } finally {
      setTogglingSync(false)
    }
  }

  const examplePrice = 20
  const pctVal = parseFloat(pct) / 100 || 0
  const fixedVal = parseFloat(fixed) || 0
  const priceWithIva = examplePrice * 1.21
  const surcharge = priceWithIva * pctVal + fixedVal
  const ebayPrice = priceWithIva + surcharge

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await ebayApi.updateConfig('surcharge_pct', (parseFloat(pct) / 100).toString())
      await ebayApi.updateConfig('surcharge_fixed', fixed)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      console.error('Save error:', e)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>

  return (
    <div className="max-w-2xl space-y-6">
      {/* Sync automático Factusol → eBay */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Sincronización automática</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Sync Factusol → eBay</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Al ejecutar la sincronización diaria, actualizar stock y precios en eBay automáticamente.
                También auto-publica productos nuevos y retira los que no tienen stock.
              </p>
            </div>
            <button
              onClick={handleToggleSync}
              disabled={togglingSync}
              className="flex-shrink-0 ml-4"
            >
              {togglingSync ? (
                <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
              ) : syncEnabled ? (
                <ToggleRight className="w-10 h-10 text-green-500 cursor-pointer" />
              ) : (
                <ToggleLeft className="w-10 h-10 text-gray-400 cursor-pointer" />
              )}
            </button>
          </div>
          {syncEnabled && (
            <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Máximo de productos nuevos por sync
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="500"
                  value={maxNewPerSync}
                  onChange={(e) => setMaxNewPerSync(e.target.value)}
                  className="w-32 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={async () => {
                    await ebayApi.updateConfig('ebay_max_new_per_sync', maxNewPerSync)
                    setSaved(true)
                    setTimeout(() => setSaved(false), 3000)
                  }}
                  className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
                >
                  <Save className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Limita cuántos productos nuevos se publican en cada ejecución para evitar saturar la API.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Anuncios patrocinados */}
      <PromotedListingsCard />

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-6">Sobrecoste eBay</h3>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sobrecoste porcentual (%)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sobrecoste fijo
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={fixed}
                onChange={(e) => setFixed(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Ejemplo en tiempo real</p>
          <div className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
            <p>Producto a <strong>{examplePrice.toFixed(2)}€</strong> sin IVA</p>
            <p className="flex items-center gap-1"><ChevronRight className="w-3 h-3" /> Con IVA: {priceWithIva.toFixed(2)}€</p>
            <p className="flex items-center gap-1"><ChevronRight className="w-3 h-3" /> Sobrecoste: {priceWithIva.toFixed(2)} x {pct}% + {fixedVal.toFixed(2)}€ = <strong>{surcharge.toFixed(2)}€</strong></p>
            <p className="text-base font-bold mt-2">Precio eBay: {ebayPrice.toFixed(2)}€</p>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4" /> Guardado
            </span>
          )}
        </div>
      </div>

      {/* Stock cap info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">Stock cap</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Todos los productos se publican con <strong>cantidad = 1</strong> cuando hay stock disponible
          (0 si no hay stock). Esto evita superar el limite de euros en eBay.
        </p>
      </div>
    </div>
  )
}

// =========================================================================
// Selection Tab
// =========================================================================

function SelectionTab() {
  const [summary, setSummary] = useState<any>(null)
  const [selection, setSelection] = useState<SelectionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [calculating, setCalculating] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [publishResult, setPublishResult] = useState<any>(null)

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true)
      const res = await ebayApi.getSelectionSummary()
      setSummary(res)
    } catch (e) {
      console.error('Summary load error:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadSummary() }, [loadSummary])

  const handleCalculate = async () => {
    setCalculating(true)
    setSelection(null)
    try {
      const res = await ebayApi.calculateSelection(1468)
      setSelection(res)
      await loadSummary()
    } catch (e) {
      console.error('Calculate error:', e)
    } finally {
      setCalculating(false)
    }
  }

  const handlePublish = async (dryRun: boolean) => {
    setPublishing(true)
    setPublishResult(null)
    try {
      const res = await ebayApi.publishSelection(dryRun)
      setPublishResult(res)
      if (!dryRun) await loadSummary()
    } catch (e) {
      console.error('Publish error:', e)
    } finally {
      setPublishing(false)
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>

  return (
    <div className="space-y-6">
      {/* Layer summary */}
      {selection && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard label="Layer A (unicas)" value={selection.layer_a_count} icon={Package} color="blue" subtitle="1 pagina por ref" />
          <KpiCard label="Layer B (top sellers)" value={selection.layer_b_count} icon={TrendingUp} color="green" subtitle="Paginas individuales" />
          <KpiCard label="Layer C (consolidadas)" value={selection.layer_c_count} icon={Layers} color="purple" subtitle="Fichas IA multi-pagina" />
          <KpiCard label="Total slots" value={selection.total_slots} icon={ShoppingCart} color="amber" subtitle={`de ${1468} max`} />
        </div>
      )}

      {/* Current state */}
      {summary && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estado actual</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Refs consolidadas</p>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.consolidated_refs}</p>
            </div>
            <div>
              <p className="text-gray-500">Paginas top seller</p>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.top_seller_pages}</p>
            </div>
            <div>
              <p className="text-gray-500">Total paginas consolidadas</p>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.total_consolidated_pages}</p>
            </div>
            <div>
              <p className="text-gray-500">Publicados</p>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.published_listings}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Acciones</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCalculate}
            disabled={calculating}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Recalcular seleccion
          </button>
          <button
            onClick={() => handlePublish(true)}
            disabled={publishing}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Dry Run
          </button>
          <button
            onClick={() => handlePublish(false)}
            disabled={publishing}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Publicar todo
          </button>
        </div>

        {publishResult && (
          <div className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
            <pre className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
              {JSON.stringify(publishResult, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}

// =========================================================================
// AI Content Tab
// =========================================================================

function AiContentTab() {
  const [aiStatus, setAiStatus] = useState<AiStatusData | null>(null)
  const [content, setContent] = useState<AiContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState<string | null>(null) // sku or 'all'
  const [previewSku, setPreviewSku] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<AiContentItem | null>(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [statusRes, contentRes] = await Promise.all([
        ebayApi.getAiStatus(),
        ebayApi.getAiContent(500),
      ])
      setAiStatus(statusRes)
      setContent(contentRes.content || [])
    } catch (e) {
      console.error('AI data load error:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleGenerate = async (sku: string) => {
    setGenerating(sku)
    try {
      await ebayApi.generateAi(sku)
      await loadData()
    } catch (e) {
      console.error(`Generate error for ${sku}:`, e)
    } finally {
      setGenerating(null)
    }
  }

  const handleGenerateAll = async () => {
    setGenerating('all')
    try {
      await ebayApi.generateAllAi()
      await loadData()
    } catch (e) {
      console.error('Generate all error:', e)
    } finally {
      setGenerating(null)
    }
  }

  const handlePreview = async (sku: string) => {
    if (previewSku === sku) {
      setPreviewSku(null)
      setPreviewData(null)
      return
    }
    try {
      const res = await ebayApi.getAiContentDetail(sku)
      setPreviewData(res.content)
      setPreviewSku(sku)
    } catch (e) {
      console.error(`Preview error for ${sku}:`, e)
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>

  return (
    <div className="space-y-6">
      {/* AI Stats */}
      {aiStatus && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard label="Generadas" value={aiStatus.total_generated} icon={CheckCircle2} color="green" />
          <KpiCard label="Pendientes" value={aiStatus.pending} icon={Clock} color="amber" />
          <KpiCard label="Total consolidadas" value={aiStatus.total_consolidated} icon={Layers} color="blue" />
          <KpiCard label="Tokens usados" value={aiStatus.total_tokens.toLocaleString()} icon={Zap} color="purple" />
          <KpiCard label="Coste total" value={`${aiStatus.total_cost_eur.toFixed(2)}€`} icon={DollarSign} color="rose" />
        </div>
      )}

      {/* Generate All button */}
      {aiStatus && aiStatus.pending > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">
                {aiStatus.pending} referencias pendientes de generar
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                Coste estimado: ~{(aiStatus.pending * 0.009).toFixed(2)}€ (Gemini 2.5 Flash)
              </p>
            </div>
            <button
              onClick={handleGenerateAll}
              disabled={generating !== null}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 font-medium"
            >
              {generating === 'all' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generar todo
            </button>
          </div>
        </div>
      )}

      {/* Content table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-white">Contenido IA generado ({content.length})</h3>
        </div>
        {content.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay contenido IA generado aun. Ejecuta "Recalcular seleccion" primero para detectar las refs Layer C.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400">SKU</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Titulo generado</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400 text-center">Paginas</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Modelo</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400 text-right">Coste</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400 text-center">Fecha</th>
                  <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-400 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {content.map((item) => (
                  <>
                    <tr key={item.sku} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 font-mono text-xs font-medium text-blue-600 dark:text-blue-400">{item.sku}</td>
                      <td className="px-4 py-3 text-gray-900 dark:text-white max-w-xs truncate">{item.title}</td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{item.product_count}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{item.model_used}</td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item.cost_eur?.toFixed(4)}€</td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">
                        {item.generated_at ? new Date(item.generated_at).toLocaleDateString('es-ES') : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handlePreview(item.sku)}
                            className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700"
                            title="Ver preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleGenerate(item.sku)}
                            disabled={generating !== null}
                            className="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700"
                            title="Regenerar"
                          >
                            {generating === item.sku
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <RotateCcw className="w-4 h-4" />
                            }
                          </button>
                        </div>
                      </td>
                    </tr>
                    {previewSku === item.sku && previewData && (
                      <tr key={`${item.sku}-preview`}>
                        <td colSpan={7} className="px-4 py-4 bg-blue-50 dark:bg-blue-900/10">
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Titulo</p>
                              <p className="font-medium text-gray-900 dark:text-white">{previewData.title}</p>
                            </div>
                            {previewData.aspects && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Aspects</p>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(previewData.aspects).map(([key, vals]) => (
                                    <span key={key} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 text-xs border">
                                      <strong>{key}:</strong> {Array.isArray(vals) ? vals.join(', ') : vals}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Descripcion HTML</p>
                              <div
                                className="prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-gray-800 rounded-lg p-3 border max-h-64 overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: previewData.description_html }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// =========================================================================
// Sub-Listings Tab (Buscabilidad)
// =========================================================================

function SubListingsTab() {
  const [stats, setStats] = useState<any>(null)
  const [refs, setRefs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [calculating, setCalculating] = useState(false)
  const [expandedSku, setExpandedSku] = useState<string | null>(null)
  const [subDetails, setSubDetails] = useState<any>(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const data = await ebayApi.getSubListings()
      setStats(data.stats)
      setRefs(data.refs || [])
    } catch {
      // No sub-listings yet
      setStats({ refs_split: 0, total_sub_listings: 0, total_pns_searchable: 0, extra_slots_used: 0 })
      setRefs([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleCalculateAll = async () => {
    setCalculating(true)
    try {
      await ebayApi.calculateAllSubListings()
      await loadData()
    } catch (e: any) {
      alert('Error: ' + (e.response?.data?.detail || e.message))
    } finally {
      setCalculating(false)
    }
  }

  const handleExpandSku = async (baseSku: string) => {
    if (expandedSku === baseSku) {
      setExpandedSku(null)
      setSubDetails(null)
      return
    }
    try {
      const data = await ebayApi.getSubListingsForSku(baseSku)
      setSubDetails(data)
      setExpandedSku(baseSku)
    } catch {
      setExpandedSku(null)
    }
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-2">
            <SplitSquareHorizontal className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-500">Refs divididas</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.refs_split || 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-500">Sub-listings totales</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.total_sub_listings || 0}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-2">
            <Search className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-500">PNs buscables</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{(stats?.total_pns_searchable || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-gray-500">Slots extra usados</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.extra_slots_used || 0}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Calcular Sub-Listings</h3>
            <p className="text-sm text-gray-500 mt-1">
              Divide las refs con mas de 30 part numbers en multiples listings para maximizar la buscabilidad en eBay.
              Cada sub-listing tiene 30 PNs en los aspectos (indexados por eBay).
            </p>
          </div>
          <button
            onClick={handleCalculateAll}
            disabled={calculating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {calculating ? 'Calculando...' : 'Calcular todos'}
          </button>
        </div>
      </div>

      {/* Table */}
      {refs.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-5 py-3 text-sm font-medium text-gray-500">Referencia</th>
                <th className="text-right px-5 py-3 text-sm font-medium text-gray-500">Sub-listings</th>
                <th className="text-right px-5 py-3 text-sm font-medium text-gray-500">PNs totales</th>
                <th className="text-right px-5 py-3 text-sm font-medium text-gray-500">Buscabilidad</th>
                <th className="text-center px-5 py-3 text-sm font-medium text-gray-500">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {refs.map((ref: any) => (
                <>
                  <tr key={ref.base_sku} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-5 py-3 font-mono text-sm font-semibold text-blue-600">{ref.base_sku}</td>
                    <td className="px-5 py-3 text-right text-sm">{ref.sub_count}</td>
                    <td className="px-5 py-3 text-right text-sm">{ref.total_pns}</td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3" /> 100%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => handleExpandSku(ref.base_sku)}
                        className="text-gray-400 hover:text-blue-500"
                      >
                        <ChevronRight className={`w-4 h-4 transition-transform ${expandedSku === ref.base_sku ? 'rotate-90' : ''}`} />
                      </button>
                    </td>
                  </tr>
                  {expandedSku === ref.base_sku && subDetails && (
                    <tr key={`${ref.base_sku}-detail`}>
                      <td colSpan={5} className="px-5 py-3 bg-gray-50 dark:bg-gray-800/20">
                        <div className="space-y-2">
                          {subDetails.sub_listings?.map((sl: any) => (
                            <div key={sl.sub_sku} className="flex items-center justify-between text-sm bg-white dark:bg-gray-900 rounded-lg px-4 py-2 border border-gray-100 dark:border-gray-800">
                              <div>
                                <span className="font-mono text-xs text-purple-600 dark:text-purple-400">{sl.sub_sku}</span>
                                <p className="text-gray-700 dark:text-gray-300 mt-0.5 text-xs truncate max-w-xl">{sl.title}</p>
                              </div>
                              <span className="text-xs text-gray-500">{sl.pn_count} PNs</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {refs.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <SplitSquareHorizontal className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay sub-listings calculados. Pulsa "Calcular todos" para empezar.</p>
        </div>
      )}
    </div>
  )
}

// =========================================================================
// Main Page
// =========================================================================

export default function EbayPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'config' | 'selection' | 'ai' | 'sublistings'>('dashboard')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">eBay Marketplace</h1>
            <p className="text-sm text-gray-500">Gestion de publicaciones, precios y fichas IA</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-1.5">
        <TabButton active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} icon={BarChart3} label="Dashboard" />
        <TabButton active={activeTab === 'config'} onClick={() => setActiveTab('config')} icon={Settings} label="Configuracion" />
        <TabButton active={activeTab === 'selection'} onClick={() => setActiveTab('selection')} icon={Layers} label="Seleccion" />
        <TabButton active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} icon={Brain} label="Contenido IA" />
        <TabButton active={activeTab === 'sublistings'} onClick={() => setActiveTab('sublistings')} icon={Search} label="Buscabilidad" />
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'config' && <ConfigTab />}
      {activeTab === 'selection' && <SelectionTab />}
      {activeTab === 'ai' && <AiContentTab />}
      {activeTab === 'sublistings' && <SubListingsTab />}
    </div>
  )
}
